import * as Yup from "yup";
import { TFunction } from "i18next";

import { Option, Filter } from "./filter";

export interface FiltersValuesList {
  role: string[];
  status: string[];
}

export interface EmployeeForEdit {
  id: string;
  role: string;
  permissions: Array<{ name: string; value: string }>;
}

export interface EditValues {
  role: string;
  permissions: Record<string, boolean>;
}

export const model = {
  role: {
    label: "Role",
    name: "role",
    placeholder: "Select role",
  },
  status: {
    label: "Status",
    name: "status",
    placeholder: "Select status",
  },
  permissions: {
    label: "Permissions for role",
    name: "permissions",
  },
} as const;


export const validationSchema = (t: TFunction, mode: "list" | "edit") => {
  if (mode === "list") {
    return Yup.object().shape({});
  }
  return Yup.object().shape({
    [model.role.name]: Yup.string().required(t("Role is required")),
    [model.permissions.name]: Yup.object().optional(),
  });
};

export const initialValues = (
  mode: "list" | "edit",
  args?: {
    employee?: EmployeeForEdit;
    roleOptions?: Option[];
  }
): FiltersValuesList | EditValues => {
  if (mode === "list") {
    return {
      [model.role.name]: [],
      [model.status.name]: [],
    };
  }

  if (mode === "edit" && args?.employee && args?.roleOptions) {
    const { employee, roleOptions } = args;

    const currentRoleValue =
      roleOptions.find((o) => o.label === employee.role)?.value ?? "";

    const permissions: EditValues["permissions"] = {};
    for (const p of employee.permissions ?? []) {
      permissions[p.name] = true;
    }

    return {
      [model.role.name]: currentRoleValue,
      [model.permissions.name]: permissions,
    };
  }

  throw new Error("initialValues: missing arguments for edit mode");
};

export const getFilters = (
  t: TFunction,
  mode: "list" | "edit",
  args?: { roleOptions: Option[]; statusOptions?: Option[]; employee?: EmployeeForEdit }
): Filter[] => {
  if (mode === "list" && args?.roleOptions && args?.statusOptions) {
    return [
      {
        type: "select-multi",
        field: { label: t(model.role.label), value: model.role.name },
        options: args.roleOptions,
      },
      {
        type: "select-multi",
        field: { label: t(model.status.label), value: model.status.name },
        options: args.statusOptions,
      },
    ];
  }

  if (mode === "edit" && args?.roleOptions && args?.employee) {
    const permOptions: Option[] = (args.employee.permissions ?? []).map((p) => ({
      label: p.value,
      value: p.name,
    }));

    return [
      {
        type: "select",
        field: { label: t(model.role.label), value: model.role.name },
        options: args.roleOptions,
      },
      {
        type: "checkbox-list",
        field: { label: t(model.permissions.label), value: model.permissions.name },
        options: permOptions,
      },
    ];
  }

  throw new Error("getFilters: invalid arguments for given mode");
};
